import DOMPurify from "dompurify";

type SanitizeHtmlOptions = {
  allowStyleTag?: boolean;
  fallbackAlt?: string;
};

type SanitizeConfig = {
  ADD_ATTR?: string[];
  ALLOWED_ATTR?: string[];
  ALLOWED_TAGS?: string[];
  FORBID_TAGS?: string[];
  [key: string]: unknown;
};

type DomPurifySanitizer = (
  input: string,
  config?: SanitizeConfig
) => string;

type DomPurifyLike = {
  sanitize: DomPurifySanitizer;
};

type DomPurifyFactory = (window: Window) => DomPurifyLike;

const BASE_FORBIDDEN_TAGS = ["base", "embed", "iframe", "meta", "object", "script"];

function getForbiddenTags(options: SanitizeHtmlOptions = {}): string[] {
  return [
    ...BASE_FORBIDDEN_TAGS,
    ...(options.allowStyleTag ? [] : ["style"]),
  ];
}

function isDomPurifyLike(value: unknown): value is DomPurifyLike {
  return typeof (value as DomPurifyLike | undefined)?.sanitize === "function";
}

function resolveSanitizerFrom(value: unknown): DomPurifySanitizer | null {
  if (isDomPurifyLike(value)) {
    return value.sanitize.bind(value);
  }

  if (typeof window === "undefined" || typeof value !== "function") {
    return null;
  }

  const instance = (value as DomPurifyFactory)(window);
  return isDomPurifyLike(instance) ? instance.sanitize.bind(instance) : null;
}

function resolveDomPurifySanitizer(): DomPurifySanitizer | null {
  const exported = DOMPurify as unknown;
  const directSanitizer = resolveSanitizerFrom(exported);

  if (directSanitizer) {
    return directSanitizer;
  }

  return resolveSanitizerFrom((exported as { default?: unknown })?.default);
}

function stripUnsafeHtmlFallback(
  input: string,
  options: SanitizeHtmlOptions = {}
): string {
  let html = input.replace(/<!--[\s\S]*?-->/g, "");

  for (const tag of getForbiddenTags(options)) {
    html = html
      .replace(new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}>`, "gi"), "")
      .replace(new RegExp(`<${tag}\\b[^>]*\\/?>`, "gi"), "");
  }

  return html
    .replace(/\s+on[a-z][\w:-]*\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/\s+srcdoc\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(
      /\s+(href|src|xlink:href|formaction)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi,
      (match, _attr: string, rawValue: string) => {
        const value = rawValue
          .replace(/^['"]|['"]$/g, "")
          .replace(/[\u0000-\u001F\u007F\s]+/g, "")
          .toLowerCase();

        if (
          value.startsWith("javascript:") ||
          value.startsWith("vbscript:") ||
          value.startsWith("data:text/html")
        ) {
          return "";
        }

        return match;
      }
    );
}

function purifyHtml(
  input: string | null | undefined,
  options: SanitizeHtmlOptions = {}
): string {
  if (!input) return "";

  const sanitizer = resolveDomPurifySanitizer();
  const config: SanitizeConfig = {
    ADD_ATTR: ["target", "rel", "alt", "title"],
    FORBID_TAGS: getForbiddenTags(options),
  };

  if (!sanitizer) {
    return stripUnsafeHtmlFallback(input, options);
  }

  return sanitizer(input, config);
}

function addBlankTargetRel(html: string, rel: string): string {
  return html.replace(
    /<a\b(?![^>]*\brel=)([^>]*)target=["']_blank["']/gi,
    `<a rel="${rel}"$1target="_blank"`
  );
}

/**
 * Overrides SEO-harmful elements inside article body HTML content:
 * 1. Remaps <h1> to <h2> while adding large heading styles so visual appearance matches
 *    author intent without breaking Google's single <h1> rule per page.
 * 2. Injects fallback alt attribute on images for Google Image Search indexing.
 */
function overrideSeoUnsafeElements(html: string, fallbackAlt: string = "Galura.id"): string {
  if (!html) return "";

  let processed = html
    .replace(/<h1(\s+[^>]*)?>/gi, (_match, attrs) => {
      const existingClassMatch = attrs ? attrs.match(/class=["']([^"']*)["']/i) : null;
      if (existingClassMatch) {
        const updatedAttrs = attrs.replace(
          /class=["']([^"']*)["']/i,
          `class="$1 text-2xl font-extrabold text-gray-900 dark:text-white leading-tight my-4"`
        );
        return `<h2${updatedAttrs}>`;
      }
      return `<h2 class="text-2xl font-extrabold text-gray-900 dark:text-white leading-tight my-4"${attrs || ""}>`;
    })
    .replace(/<\/h1>/gi, "</h2>");

  // Ensure alt attribute exists for Google Image Search
  processed = processed.replace(/<img\b(?![^>]*\balt=)([^>]*)>/gi, `<img alt="${fallbackAlt}"$1>`);
  processed = processed.replace(/alt=["'](?:\s*|image|img|photo|picture)["']/gi, `alt="${fallbackAlt}"`);

  return processed;
}

export function sanitizeHtml(
  input: string | null | undefined,
  options: SanitizeHtmlOptions = {}
): string {
  const cleaned = addBlankTargetRel(purifyHtml(input, options), "noopener noreferrer");
  return overrideSeoUnsafeElements(cleaned, options.fallbackAlt || "Galura.id");
}

export function sanitizeExternalHtml(input: string | null | undefined): string {
  const cleaned = addBlankTargetRel(purifyHtml(input), "noopener noreferrer nofollow");
  return overrideSeoUnsafeElements(cleaned, "Galura.id");
}

/**
 * Decodes HTML entities into human-readable characters.
 * Handles named entities (&nbsp;, &amp;, &quot;, etc.) and numeric decimal/hex entities.
 */
export function decodeHtmlEntities(str: string | null | undefined): string {
  if (!str) return "";
  return str
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#(\d+);/g, (_, dec) => {
      const code = Number(dec);
      return !isNaN(code) && code > 0 ? String.fromCharCode(code) : "";
    })
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => {
      const code = parseInt(hex, 16);
      return !isNaN(code) && code > 0 ? String.fromCharCode(code) : "";
    });
}

export function sanitizePlainText(input: string | null | undefined): string {
  if (!input) return "";

  // Ganti tag penutup blok atau pemisah baris dengan spasi agar teks antar-elemen tidak menempel
  const normalized = input
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)>/gi, " ")
    .replace(/<(br|hr)\s*\/?>/gi, " ");

  const sanitizer = resolveDomPurifySanitizer();
  let stripped = "";

  if (sanitizer) {
    stripped = sanitizer(normalized, {
      ALLOWED_ATTR: [],
      ALLOWED_TAGS: [],
    });
  } else {
    stripped = stripUnsafeHtmlFallback(normalized).replace(/<[^>]*>/g, "");
  }

  return decodeHtmlEntities(stripped).replace(/\s+/g, " ").trim();
}

